/**
 * Per-patient alarm sound mute.
 *
 * The top-bar button mutes the whole board (alertSound.js). This mutes ONE
 * patient: their alarms still show, blink and appear in the Connection-alerts
 * list and the alarm history — only the sound is withheld, and every other
 * patient keeps sounding.
 *
 * Deliberately kept in this browser's storage, NOT on the server:
 *   - it is synced across this browser's tabs (like the global mute), so a
 *     second Live Monitor window can't keep sounding a patient muted in the first;
 *   - it does NOT survive logout (logout clears localStorage). A silenced
 *     patient that stays silenced across shifts, for whoever logs in next, is
 *     exactly how an alarm gets missed — a fresh login starts with sound on.
 * Removing a patient from the board also clears their mute.
 */
const KEY = 'lm-muted-patients';

let muted = read();
const listeners = new Set();

function read() {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY));
    const out = {};
    if (Array.isArray(v)) v.forEach(id => { out[String(id)] = true; });
    return out;
  } catch (e) { return {}; }
}
function write() {
  try { window.localStorage.setItem(KEY, JSON.stringify(Object.keys(muted))); } catch (e) { /* storage blocked */ }
}
function notify() { listeners.forEach(fn => fn()); }

export function isPatientMuted(deviceId) { return !!muted[String(deviceId)]; }
export function mutedPatientCount() { return Object.keys(muted).length; }
export function getMutedPatients() { return Object.keys(muted); }

export function setPatientMuted(deviceId, on) {
  const id = String(deviceId);
  if (!!muted[id] === !!on) return;
  if (on) muted[id] = true; else delete muted[id];
  write();          // fires a `storage` event in every OTHER tab
  notify();
}

export function onPatientMuteChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// ── One alarm on one patient ────────────────────────────────────────────
// Click an alarm on a card to silence just that alarm for just that patient
// ("yes, I know Ravi's phone battery is low"). It lasts for the CURRENT
// episode only: when the alarm clears the mute is dropped, so the next
// occurrence sounds again. Same storage rules as above — this browser, all
// its tabs, gone at logout.
const ALARM_KEY = 'lm-muted-alarms';
let mutedAlarms = readAlarms();
function readAlarms() {
  try {
    const v = JSON.parse(window.localStorage.getItem(ALARM_KEY));
    const out = {};
    if (Array.isArray(v)) v.forEach(k => { out[String(k)] = true; });
    return out;
  } catch (e) { return {}; }
}
function writeAlarms() {
  try { window.localStorage.setItem(ALARM_KEY, JSON.stringify(Object.keys(mutedAlarms))); } catch (e) { /* storage blocked */ }
}
const alarmId = (deviceId, key) => String(deviceId) + '|' + String(key);

export function isAlarmMuted(deviceId, key) { return !!mutedAlarms[alarmId(deviceId, key)]; }
export function mutedAlarmCount() { return Object.keys(mutedAlarms).length; }
export function setAlarmMuted(deviceId, key, on) {
  const id = alarmId(deviceId, key);
  if (!!mutedAlarms[id] === !!on) return;
  if (on) mutedAlarms[id] = true; else delete mutedAlarms[id];
  writeAlarms();
  notify();
}
/** The board reports which alarms are active; mutes for alarms that ended are dropped. */
export function dropEndedAlarmMutes(deviceId, activeKeys) {
  const prefix = String(deviceId) + '|';
  const keep = {};
  (activeKeys || []).forEach(k => { keep[alarmId(deviceId, k)] = true; });
  let changed = false;
  Object.keys(mutedAlarms).forEach(id => {
    if (id.indexOf(prefix) === 0 && !keep[id]) { delete mutedAlarms[id]; changed = true; }
  });
  if (changed) { writeAlarms(); notify(); }
}


function syncFromStorage() {
  const next = read();
  const nextAlarms = readAlarms();
  const sig = o => Object.keys(o).sort().join(',');
  if (sig(muted) === sig(next) && sig(mutedAlarms) === sig(nextAlarms)) return;
  muted = next;
  mutedAlarms = nextAlarms;
  notify();
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === KEY || e.key === ALARM_KEY || e.key === null) syncFromStorage(); });
  document.addEventListener('visibilitychange', syncFromStorage);
  window.addEventListener('focus', syncFromStorage);
}
